import { FC } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useMachine } from '@xstate/react';

import { CompanyProfile } from '__generated__/GQL';
import webView from 'common/webView';
import postMessage from 'common/webView/helpers/postMessage';
import {
  CurrentTaxForecastPeriod,
  TaxForecastProfile,
} from 'features/Accounting/pages/Estimation/hooks/useTaxForecastData';
import { useTrackMachine } from 'features/Accounting/pages/Estimation/hooks/useTrackMachine';

import * as routes from '../../../../../../routes';
import microEstimationMachine, {
  MicroEstimationEventType,
} from '../../machine';
import { MicroEstimationState } from '../../machine/state';
import { getStepTotal } from '../../machine/utils';
import Accre from './components/Accre';
import CompanySituation from './components/CompanySituation';
import PrelevLib from './components/PrelevLib';
import RevenueSituation from './components/RevenueSituation';
import Success from './components/Success';
import TaxFrequency from './components/TaxFrequency';
import TaxType from './components/TaxType';
import { StepComponentProps } from './components/types';
import Vat from './components/Vat';

interface MicroEstimationFlowProps {
  taxForecastProfile?: TaxForecastProfile;
  taxForecastPeriod?: CurrentTaxForecastPeriod;
  companyProfile: Pick<
    CompanyProfile,
    'hasAccre' | 'hasPrelevLib' | 'taxFrequency' | 'companyProfileId'
  >;
}

export const ESTIMATION_REVENUE_EDIT = 'revenueEdit';

const MicroEstimationFlow: FC<MicroEstimationFlowProps> = ({
  companyProfile,
  taxForecastPeriod,
  taxForecastProfile,
}) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const revenueEdit = queryParams.get(ESTIMATION_REVENUE_EDIT);

  const [current, send, machineService] = useMachine(microEstimationMachine, {
    actions: {
      navigateToOverview: () => {
        if (webView.isInWebView) {
          postMessage({ action: 'CLOSE_WEBVIEW_REQUESTED' });
        } else {
          history.push(routes.estimation);
        }
      },
    },
    context: {
      companyProfile,
      // Defines if we only show the revenue step or all the others
      isRevenueEdit: revenueEdit === 'true',
      stepProgress: { current: 0, total: getStepTotal() },
      taxForecastPeriod,
      taxForecastProfile,
    },
  });

  useTrackMachine(machineService);

  const commonProps: Omit<StepComponentProps<unknown>, 'onSave'> = {
    context: current.context,
    onBack: () => send({ type: MicroEstimationEventType.BACK }),
    onNext: () => send({ type: MicroEstimationEventType.NEXT }),
  };

  const renderStepComponent = () => {
    switch (current.value as MicroEstimationState) {
      case MicroEstimationState.TAX_FREQUENCY:
        return (
          <TaxFrequency
            {...commonProps}
            onSave={(data) =>
              send({
                data,
                type: MicroEstimationEventType.SAVE_TAX_FREQUENCY_STEP,
              })
            }
          />
        );
      case MicroEstimationState.VAT:
        return (
          <Vat
            {...commonProps}
            onSave={(data) =>
              send({
                data,
                type: MicroEstimationEventType.SAVE_VAT_STEP,
              })
            }
          />
        );
      case MicroEstimationState.REVENUE_SITUATION: {
        return (
          <RevenueSituation
            {...commonProps}
            onRevenueFetched={(revenue: number) =>
              send({ revenue, type: MicroEstimationEventType.UPDATE_REVENUE })
            }
            onSave={(data) =>
              send({
                data,
                type: MicroEstimationEventType.SAVE_REVENUE_SITUATION_STEP,
              })
            }
          />
        );
      }
      case MicroEstimationState.COMPANY_SITUATION:
        return (
          <CompanySituation
            {...commonProps}
            onSave={(data) =>
              send({
                data,
                type: MicroEstimationEventType.SAVE_COMPANY_SITUATION_STEP,
              })
            }
          />
        );
      case MicroEstimationState.PRELEV_LIB:
        return (
          <PrelevLib
            {...commonProps}
            onSave={(data) =>
              send({
                data,
                type: MicroEstimationEventType.SAVE_PRELEV_LIB_STEP,
              })
            }
          />
        );
      case MicroEstimationState.ACCRE:
        return (
          <Accre
            {...commonProps}
            onSave={(data) =>
              send({ data, type: MicroEstimationEventType.SAVE_ACCRE_STEP })
            }
          />
        );
      case MicroEstimationState.TAX_TYPE:
        return (
          <TaxType
            {...commonProps}
            onSave={(data) =>
              send({ data, type: MicroEstimationEventType.SAVE_TAX_TYPE_STEP })
            }
          />
        );
      case MicroEstimationState.SUCCESS:
        return (
          <Success
            context={current.context}
            onBack={() => send({ type: MicroEstimationEventType.BACK })}
            onFinish={() => send({ type: MicroEstimationEventType.FINISH })}
          />
        );
      default:
        return null;
    }
  };

  return renderStepComponent();
};

export default MicroEstimationFlow;
